import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';

@Injectable()
export class RetryHttpInterceptor implements HttpInterceptor {

  // Set the number of retry attempts
  private readonly maxRetries: number = 3;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Opt-out for requests that must never be replayed — an OAuth callback
    // spends a single-use authorization code, so a second attempt can turn a
    // recoverable failure into a permanently consumed one.
    if (req.headers.has('skipRetry')) {
      return next.handle(req).pipe(catchError(this.handleError));
    }

    return next.handle(req).pipe(
      retry({
        count: this.maxRetries,
        // Only replay what a replay can actually fix. Retrying a 4xx just sends
        // the same rejected request three more times before surfacing the error,
        // and on a non-idempotent POST it can do real damage.
        delay: (error: unknown) => {
          if (this.isTransient(error)) {
            return timer(0); // signal "retry now"; matches the previous immediate-retry behaviour
          }
          return throwError(() => error);
        }
      }),
      catchError(this.handleError) // Handle the error after retries are exhausted
    );
  }

  /**
   * Network failures (status 0) and server errors; never client errors.
   *
   * 503 is excluded deliberately. This API uses it to mean "this deployment is
   * not configured for that feature" — a standing condition a retry cannot
   * clear, so replaying it just multiplies the failure. 502 stays retryable:
   * that one really is an upstream being briefly unreachable.
   */
  private isTransient(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    return error.status === 0 || (error.status >= 500 && error.status !== 503);
  }

  // A helper function to handle errors
  private handleError(error: HttpErrorResponse): Observable<never> {
    // Here you can log the error, show a notification, or return a custom error message
    console.error('Request failed after retries:', error.message);
    return throwError(() => error);
  }
}
